import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Zap } from 'lucide-react';

const DynamicPricingSimulation: React.FC = () => {
  const [basePrice, setBasePrice] = useState(100);
  const [baseDemand, setBaseDemand] = useState(1000);
  const [elasticity, setElasticity] = useState(-2.0);
  const [unitCost, setUnitCost] = useState(60);
  const [competitorPrice, setCompetitorPrice] = useState(105);
  const [priceRange, setPriceRange] = useState({ min: 70, max: 130 });
  const [simulationData, setSimulationData] = useState<any[]>([]);
  const [optimalPrice, setOptimalPrice] = useState(100);
  const [optimalMetrics, setOptimalMetrics] = useState({
    revenue: 0,
    profit: 0,
    demand: 0,
    margin: 0,
  });

  useEffect(() => {
    runSimulation();
  }, [basePrice, baseDemand, elasticity, unitCost, competitorPrice, priceRange]);

  const calculateDemand = (price: number) => {
    const priceRatio = price / basePrice;
    const ownPriceEffect = Math.pow(priceRatio, elasticity);
    
    // Cross-price elasticity effect (competitor)
    const crossElasticity = 0.5;
    const compRatio = competitorPrice / basePrice;
    const crossPriceEffect = Math.pow(compRatio, crossElasticity);
    
    return baseDemand * ownPriceEffect * crossPriceEffect;
  };

  const runSimulation = () => {
    const data = [];
    const step = (priceRange.max - priceRange.min) / 50;
    let maxProfit = -Infinity;
    let bestPrice = basePrice;

    for (let price = priceRange.min; price <= priceRange.max; price += step) {
      const demand = calculateDemand(price);
      const revenue = price * demand;
      const cost = unitCost * demand;
      const profit = revenue - cost;
      const margin = ((price - unitCost) / price) * 100;

      data.push({
        price: Math.round(price * 100) / 100,
        demand: Math.round(demand),
        revenue: Math.round(revenue),
        profit: Math.round(profit),
        margin: Math.round(margin * 10) / 10,
      });

      if (profit > maxProfit && price >= unitCost * 1.15) { // Min 15% margin
        maxProfit = profit;
        bestPrice = price;
      }
    }

    setSimulationData(data);
    setOptimalPrice(Math.round(bestPrice * 100) / 100);
    
    const optDemand = calculateDemand(bestPrice);
    setOptimalMetrics({
      revenue: Math.round(bestPrice * optDemand),
      profit: Math.round((bestPrice - unitCost) * optDemand),
      demand: Math.round(optDemand),
      margin: Math.round(((bestPrice - unitCost) / bestPrice) * 1000) / 10,
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const currentDemand = Math.round(calculateDemand(basePrice));
  const currentRevenue = Math.round(basePrice * currentDemand);
  const currentProfit = Math.round((basePrice - unitCost) * currentDemand);
  const currentMargin = Math.round(((basePrice - unitCost) / basePrice) * 1000) / 10;

  const revenueLift = ((optimalMetrics.revenue - currentRevenue) / currentRevenue) * 100;
  const profitLift = ((optimalMetrics.profit - currentProfit) / currentProfit) * 100;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
          <Zap className="w-6 h-6 mr-2 text-blue-600" />
          Interactive Price Elasticity Simulator
        </h3>
        <p className="text-gray-600">
          Adjust parameters to see how price changes affect demand, revenue, and profit based on elasticity modeling
        </p>
      </div>

      {/* Input Parameters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="section-card">
          <h4 className="font-semibold text-gray-800 mb-4">Product Parameters</h4>
          <div className="space-y-4">
            <div>
              <label className="label">Current Price ($)</label>
              <input
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(Number(e.target.value))}
                className="input-field"
                min="1"
                step="1"
              />
            </div>
            <div>
              <label className="label">Base Demand (units)</label>
              <input
                type="number"
                value={baseDemand}
                onChange={(e) => setBaseDemand(Number(e.target.value))}
                className="input-field"
                min="100"
                step="100"
              />
            </div>
            <div>
              <label className="label">Unit Cost ($)</label>
              <input
                type="number"
                value={unitCost}
                onChange={(e) => setUnitCost(Number(e.target.value))}
                className="input-field"
                min="1"
                step="1"
              />
            </div>
          </div>
        </div>

        <div className="section-card">
          <h4 className="font-semibold text-gray-800 mb-4">Market Parameters</h4>
          <div className="space-y-4">
            <div>
              <label className="label">Price Elasticity</label>
              <input
                type="number"
                value={elasticity}
                onChange={(e) => setElasticity(Number(e.target.value))}
                className="input-field"
                min="-5"
                max="-0.1"
                step="0.1"
              />
              <div className="text-xs text-gray-500 mt-1">
                {elasticity < -2.5 ? 'Highly Elastic' :
                 elasticity < -1.5 ? 'Elastic' :
                 elasticity < -0.8 ? 'Unit Elastic' : 'Inelastic'}
              </div>
            </div>
            <div>
              <label className="label">Competitor Price ($)</label>
              <input
                type="number"
                value={competitorPrice}
                onChange={(e) => setCompetitorPrice(Number(e.target.value))}
                className="input-field"
                min="1"
                step="1"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Min Price ($)</label>
                <input
                  type="number"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange({ ...priceRange, min: Number(e.target.value) })}
                  className="input-field"
                  min="1"
                />
              </div>
              <div>
                <label className="label">Max Price ($)</label>
                <input
                  type="number"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({ ...priceRange, max: Number(e.target.value) })}
                  className="input-field"
                  min="1"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="section-card bg-gradient-to-br from-green-50 to-blue-50">
          <h4 className="font-semibold text-gray-800 mb-4">Optimal Price</h4>
          <div className="text-center mb-4">
            <div className="text-4xl font-bold text-green-600">
              {formatCurrency(optimalPrice)}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              vs. Current: {formatCurrency(basePrice)}
            </div>
            <div className={`text-lg font-semibold mt-2 ${
              optimalPrice > basePrice ? 'text-green-600' : 'text-red-600'
            }`}>
              {optimalPrice > basePrice ? '+' : ''}{((optimalPrice - basePrice) / basePrice * 100).toFixed(1)}%
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Revenue Lift:</span>
              <span className="font-semibold text-green-600">{revenueLift > 0 ? '+' : ''}{revenueLift.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Profit Lift:</span>
              <span className="font-semibold text-blue-600">{profitLift > 0 ? '+' : ''}{profitLift.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Margin:</span>
              <span className="font-semibold text-purple-600">{optimalMetrics.margin.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Current vs Optimal Comparison */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="text-sm text-gray-600 mb-1">Revenue</div>
          <div className="text-xl font-bold text-gray-900">{formatCurrency(currentRevenue)}</div>
          <div className="text-xs text-green-600 mt-1">
            Optimal: {formatCurrency(optimalMetrics.revenue)}
          </div>
        </div>
        <div className="stat-card">
          <div className="text-sm text-gray-600 mb-1">Profit</div>
          <div className="text-xl font-bold text-gray-900">{formatCurrency(currentProfit)}</div>
          <div className="text-xs text-blue-600 mt-1">
            Optimal: {formatCurrency(optimalMetrics.profit)}
          </div>
        </div>
        <div className="stat-card">
          <div className="text-sm text-gray-600 mb-1">Demand</div>
          <div className="text-xl font-bold text-gray-900">{currentDemand.toLocaleString()}</div>
          <div className="text-xs text-purple-600 mt-1">
            Optimal: {optimalMetrics.demand.toLocaleString()}
          </div>
        </div>
        <div className="stat-card">
          <div className="text-sm text-gray-600 mb-1">Margin</div>
          <div className="text-xl font-bold text-gray-900">{currentMargin}%</div>
          <div className="text-xs text-orange-600 mt-1">
            Optimal: {optimalMetrics.margin}%
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Profit Curve */}
        <div className="section-card">
          <h4 className="font-semibold text-gray-800 mb-4">Revenue & Profit Curves</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={simulationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="price" 
                label={{ value: 'Price ($)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Revenue"
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#3b82f6" 
                strokeWidth={2}
                name="Profit"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Demand Curve */}
        <div className="section-card">
          <h4 className="font-semibold text-gray-800 mb-4">Demand Curve</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={simulationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="price" 
                label={{ value: 'Price ($)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: 'Demand (units)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="demand" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                name="Demand"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="section-card bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
          Key Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold text-gray-700">Price Sensitivity:</span>
            <p className="text-gray-600 mt-1">
              With elasticity of {elasticity.toFixed(2)}, a 10% price {optimalPrice > basePrice ? 'increase' : 'decrease'} would 
              result in approximately {Math.abs(elasticity * 10).toFixed(1)}% demand {optimalPrice > basePrice ? 'decrease' : 'increase'}.
            </p>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Competitive Position:</span>
            <p className="text-gray-600 mt-1">
              Optimal price is {((optimalPrice / competitorPrice - 1) * 100).toFixed(1)}% 
              {optimalPrice > competitorPrice ? ' above' : ' below'} competitor price, 
              balancing profitability with market competitiveness.
            </p>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Revenue Impact:</span>
            <p className="text-gray-600 mt-1">
              Moving to optimal price could {revenueLift > 0 ? 'increase' : 'decrease'} revenue by {formatCurrency(Math.abs(optimalMetrics.revenue - currentRevenue))} 
              ({Math.abs(revenueLift).toFixed(1)}%).
            </p>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Profit Optimization:</span>
            <p className="text-gray-600 mt-1">
              Optimal pricing maximizes profit at {formatCurrency(optimalMetrics.profit)}, 
              a {Math.abs(profitLift).toFixed(1)}% {profitLift > 0 ? 'improvement' : 'change'} from current levels.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicPricingSimulation;
